// Los mensajes de una conversación del Agente, sin la barra de entrada
// (ADR-2026-10-12-la-conversacion-del-asistente-en-crear).
//
// Salió de `ConversacionChatAtlas` para que otra pantalla pueda mostrar la MISMA
// conversación con su propia caja: el Asistente de Crear escribe desde la caja de Crear y
// ve acá lo que responde Cumbre. Es todo lo que el Agente dibuja entre el arranque y el
// composer, sin cambiar nada:
//   • las burbujas y las tarjetas del asistente (`MessageRow`) con sus extras (gráfico,
//     sugerencias del turno);
//   • Cumbre al pie, trabajando o quieta, con la etiqueta de lo que hace;
//   • el scroll que sigue la respuesta y el botón de "ir al final";
//   • la línea de lo que se genera en OTRO lado y el slot de la card activa.
// Lee el chat del contexto: no hay un segundo motor ni un segundo estado.

import { useEffect, useMemo, useRef } from 'react';
import { ArrowDown } from 'lucide-react';
import { useAvisoGenerando, PuntoPulsante } from '../../components/AvisoGenerandoDeFondo';
import { LineasDeAcciones, MessageRow, ToolCardRenderer } from '../../pages/v2/ChatPanelV2';
import { filasDeLaConversacion } from '../../pages/v2/accionesDelTurno';
import { ProveedorDeSelectorDeReferencias } from './ProveedorDeSelectorDeReferencias';
import { useRegistrarPantallaDeCarga } from '../../pages/v2/useGeneracionesEnCurso';
import { isInlinePendingCard } from '../../pages/v2/restaurarToolCards';
import { esEncuesta } from './encuestaEnLaCaja';
import { esperaAlUsuario } from '../../pages/v2/actividadDelAgente';
import { useChatAtlas } from '../ChatAtlasContext';
import { hayGeneracionEnVuelo } from '../lienzo/tandasDelLienzo';
import { ExtrasBurbuja, type VistaGrafico } from './BurbujaMensaje';
import { MascotaAgente } from './MascotaAgente';
import { sugerenciasDelTurno } from './sugerenciasDelTurno';
import { useScrollDeConversacion } from './useScrollDeConversacion';

export interface PropsMensajesDeLaConversacion {
  /** Ancho de la columna de mensajes. */
  anchoColumna?: string;
  /** Padding lateral. */
  padding?: string;
  /** Qué hace la tarjeta de una respuesta gráfica. */
  vistaGrafico?: VistaGrafico;
  /** Se llama con el id del gráfico al tocar su tarjeta. */
  onAbrirGrafico?: (id: string) => void;
  /**
   * Un contador que sube cada vez que el usuario manda algo desde su caja: la
   * conversación vuelve a seguir la respuesta y baja al fondo (el usuario quiere ver lo
   * suyo). La caja vive afuera, así que el aviso entra por acá.
   */
  pedidoDeIrAlFondo?: number;
  /** Los textos en globos blancos (los dos lados): para dibujarla sobre un fondo de color. */
  globosBlancos?: boolean;
  /**
   * Al tocar una sugerencia del turno. Completa la caja de la pantalla, NO manda: el usuario
   * la retoca antes de enviarla (ADR-2026-10-20-las-sugerencias-del-turno-completan-la-caja).
   * Obligatorio porque la caja es de quien monta la lista (el Agente escribe en
   * `chat.borrador`; Crear, en su propio prompt).
   */
  onSugerencia: (texto: string) => void;
  /**
   * La pantalla dibuja la encuesta en el lugar de su caja (`EncuestaEnLaCaja`): acá no va. Sin esto
   * (default), la encuesta va en el slot de arriba como siempre. Sacarla de acá sin dibujarla allá la
   * deja invisible y el turno esperando (ADR-2026-10-22-la-encuesta-reemplaza-la-caja-de-texto-2).
   */
  encuestaEnLaCaja?: boolean;
}

export const MensajesDeLaConversacion: React.FC<PropsMensajesDeLaConversacion> = ({
  anchoColumna = 'max-w-3xl',
  padding = 'px-4 sm:px-6',
  vistaGrafico = 'ir-a-pantalla',
  onAbrirGrafico,
  pedidoDeIrAlFondo = 0,
  globosBlancos = false,
  onSugerencia,
  encuestaEnLaCaja = false,
}) => {
  const chat = useChatAtlas();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // El scroll (ADR-2026-09-13-el-scroll-del-agente): vuelve a donde quedó ESTE chat, sigue
  // la respuesta mientras llega, se detiene si el usuario sube, y ofrece el botón de "ir al
  // final". La clave es el id del chat; un chat nuevo todavía sin id comparte un marcador
  // (está en el fondo igual).
  const { lejosDelFondo, irAlFondo, alEnviar } = useScrollDeConversacion({
    scrollRef,
    clave: chat.motor.getChatId() ?? 'sin-chat',
    activo: chat.mensajes.length > 0,
  });
  useEffect(() => {
    if (pedidoDeIrAlFondo > 0) alEnviar();
  }, [pedidoDeIrAlFondo, alEnviar]);

  const ultimo = chat.mensajes[chat.mensajes.length - 1];
  /**
   * ¿El agente está trabajando, o me está esperando a mí?
   *
   * No alcanza con "hay una card pendiente": el lote abierto queda pendiente MIENTRAS el
   * agente le escribe los prompts del set siguiente, que es la parte más larga del turno
   * (ADR-2026-09-14-el-lote-abierto-no-es-una-card-que-espera). Con la regla vieja la
   * mascota se congelaba y la etiqueta —que el motor venía calculando bien, "Escribiendo
   * el prompt 7…"— no se dibujaba, así que el chat parecía colgado.
   */
  const trabajando = chat.streaming && !esperaAlUsuario(chat.motor.activeToolCard);
  // "Pensando…": está trabajando y todavía no hay nada visible (ni texto ni tarjeta).
  const pensando =
    trabajando &&
    (!ultimo ||
      ultimo.role === 'user' ||
      (!ultimo.text && (!ultimo.toolCards || ultimo.toolCards.length === 0)));
  // EL AVISO DE "GENERANDO DE FONDO" NO SE MONTA SOBRE EL CHAT
  // (ADR-2026-10-05-el-aviso-de-generando-no-se-monta-sobre-el-chat). Con mensajes, el
  // composer vive abajo, justo donde flota el aviso: la conversación se registra como
  // pantalla que muestra la carga —lo que genera el chat ya se ve en sus cards— y lo que
  // se genera en OTRO lado se cuenta en una línea arriba del composer, en vez de flotar
  // encima.
  useRegistrarPantallaDeCarga(true);
  const generandoEnElChat = useMemo(() => hayGeneracionEnVuelo(chat.mensajes), [chat.mensajes]);
  const avisoDeFondo = useAvisoGenerando({ ignorarPantallas: true });
  const lineaDeFondo = !generandoEnElChat ? avisoDeFondo : null;
  // Las sugerencias del turno, siempre debajo del ÚLTIMO mensaje del asistente
  // (ADR-2026-10-05-las-sugerencias-van-al-final-del-turno).
  const sugerencias = useMemo(() => sugerenciasDelTurno(chat.mensajes), [chat.mensajes]);
  const filas = useMemo(() => filasDeLaConversacion(chat.mensajes), [chat.mensajes]);
  // Lo que va al slot: la card activa que NO se dibuja inline. Las inline-pendientes
  // (propuestas de generación, galerías, planilla/PDF) ya viven en el stream y ponerlas
  // acá las mostraría dos veces — el mismo reparto que hace `AsistenteMode`.
  const cardActiva =
    chat.motor.activeToolCard &&
    !isInlinePendingCard(chat.motor.activeToolCard) &&
    // La encuesta no va acá si la pantalla la pone en el lugar de su caja.
    !(encuestaEnLaCaja && esEncuesta(chat.motor.activeToolCard))
      ? chat.motor.activeToolCard
      : null;

  return (
    // Las cards de imagen suman referencias con el selector de fuentes
    // (ADR-2026-10-20-las-referencias-de-la-card-se-editan).
    <ProveedorDeSelectorDeReferencias>
      {/* El scroller va envuelto para que el botón de "ir al final" flote sobre él, no
          sobre el composer. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={scrollRef}
          // `overscroll-contain`: llegado al final, la rueda NO sigue scrolleando la
          // pantalla de atrás. Abajo hay más aire que arriba (`pb-20`): la última
          // respuesta y la mascota no quedan pegadas al composer.
          className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${padding} pt-6 pb-20`}
          style={{ scrollbarWidth: 'thin' }}
        >
          <div className={`mx-auto flex flex-col gap-5 ${anchoColumna}`}>
            {/* Las rondas que sólo hicieron acciones son líneas de lo que hizo, juntas; un mensaje
                vacío no se dibuja (era un hueco que bajaba la mascota un renglón por acción):
                ADR-2026-10-22-cada-accion-del-asistente-queda-en-una-linea. */}
            {filas.map((f) => {
              if (f.tipo === 'acciones') {
                return (
                  <div key={f.clave} className="-mt-2 px-0.5">
                    <LineasDeAcciones lineas={f.lineas} />
                  </div>
                );
              }
              const i = f.indice;
              const m = chat.mensajes[i]!;
              return (
                <div key={i} className="flex flex-col gap-3">
                  <MessageRow msg={m} chat={chat.motor} showPendingCards globosBlancos={globosBlancos} />
                  {m.role === 'assistant' && (
                    <ExtrasBurbuja
                      mensaje={m}
                      sugerencias={sugerencias?.indice === i ? sugerencias.sugerencias : undefined}
                      bloqueado={chat.streaming}
                      onSugerencia={onSugerencia}
                      vistaGrafico={vistaGrafico}
                      onAbrirGrafico={onAbrirGrafico}
                      graficoActivoId={chat.graficoActivoId}
                    />
                  )}
                </div>
              );
            })}

            {/* Cumbre al pie de la conversación (ADR-2026-09-12-la-mascota-acompana-la-
                conversacion): quieta mientras espera; desde que el usuario manda un mensaje
                cruza y gira en la pista corta, con la etiqueta de lo que está haciendo
                debajo. Con una card activa esperando el click no hay nada que animar. */}
            <MascotaAgente
              modo={trabajando ? 'trabajando' : 'quieta'}
              etiqueta={chat.actividad ?? (pensando ? 'Pensando…' : null)}
            />
          </div>
        </div>

        {/* "Ir al final": aparece cuando el usuario subió. Redondo, blanco, con sombra, en
            el medio y sobre el borde de abajo del scroller. */}
        {lejosDelFondo && (
          <button
            type="button"
            onClick={irAlFondo}
            aria-label="Ir al final de la conversación"
            className="absolute bottom-4 left-1/2 z-[5] inline-flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full bg-white text-[rgb(var(--brand-black-rgb)_/_0.7)] shadow-[0_4px_16px_rgb(var(--brand-black-rgb)_/_0.18)] ring-1 ring-black/[0.06] transition-colors hover:bg-neutral-50 hover:text-brand-black animate-fade-in"
          >
            <ArrowDown className="h-5 w-5" strokeWidth={2.25} aria-hidden="true" />
          </button>
        )}
      </div>

      {lineaDeFondo && (
        <div className={`shrink-0 ${padding} pt-1`}>
          <p
            role="status"
            aria-live="polite"
            className={`mx-auto flex items-center gap-2 px-1 text-[12.5px] font-medium text-[rgb(var(--brand-black-rgb)_/_0.55)] ${anchoColumna}`}
          >
            <PuntoPulsante className="text-brand-primary" />
            {lineaDeFondo}
          </p>
        </div>
      )}

      {cardActiva && (
        <div className={`shrink-0 ${padding} pt-2`}>
          <div
            key={cardActiva.id}
            className={`mx-auto ${anchoColumna} animate-mode-swap overflow-y-auto rounded-2xl bg-white shadow-[0_2px_14px_-6px_rgba(0,0,0,0.12),0_1px_3px_rgba(0,0,0,0.04)] ring-1 ring-black/[0.04]`}
            style={{ maxHeight: '55dvh' }}
          >
            <ToolCardRenderer card={cardActiva} chat={chat.motor} embedded />
          </div>
        </div>
      )}
    </ProveedorDeSelectorDeReferencias>
  );
};

export default MensajesDeLaConversacion;
